import { useCallback } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { AiCaptionServices } from "@/services/bedrock/AiCaptionServices";

export type ShareCaptionStatus = "none" | "pending" | "ready" | "failed";

export const shareCaptionKey = (playerId: number, reviewIds: number[]) =>
  ["shareCaption", playerId, reviewIds.join(",")] as const;

export interface ShareCaption {
  caption: string | null;
  status: ShareCaptionStatus;
  /** Asks for a new caption for the same reviews. */
  rewrite: () => void;
}

/**
 * The caption for a review post, written from the reviews alone so it can start the moment the
 * reviews are known, before any design is picked or rendered. Kept per review set, so switching
 * looks never rewrites it.
 */
export function useShareCaption(playerId: number | null, reviewIds: number[], enabled: boolean): ShareCaption {
  const queryClient = useQueryClient();
  const active = enabled && playerId != null && reviewIds.length > 0;
  const key = shareCaptionKey(playerId ?? -1, reviewIds);

  const query = useQuery({
    queryKey: key,
    queryFn: async () => (await AiCaptionServices.generate({ reviewIds, playerId: playerId as number })).caption,
    enabled: active,
    staleTime: Infinity,
    retry: false,
  });

  const rewrite = useCallback(() => {
    if (active) void queryClient.refetchQueries({ queryKey: key, exact: true });
  }, [active, queryClient, key.join("|")]);

  let status: ShareCaptionStatus = "none";
  if (active) {
    if (query.isFetching) status = "pending";
    else if (query.isError) status = "failed";
    else if (query.data) status = "ready";
    else status = "pending";
  }

  return { caption: query.data ?? null, status, rewrite };
}
